import { MetricEnvelope, readerFor, tableFrom } from './metabaseTable';
import { readingRowsOnly } from './wtr';

/**
 * The three bespoke Reversal column schemas, captured live on `api-uat.perxtech.io` 2026-09-04.
 *
 * `C-REV-001` is NOT here: it returns the standard 16-column metric row and goes through
 * `metricResult.ts` like every other metric. Only `C-REV-002` (one row per reversal EVENT),
 * `C-REV-003` (aggregated per entitlement type) and `C-REV-004` (aggregated per reason code) carry
 * shapes of their own, and none of the three has a `metric_value` or a `calculation_status`.
 * Contract PLA-8825, PLA-8828.
 *
 * All three answered `0 rows` on UAT, so every field below is typed from the envelope's own `cols`
 * metadata and NO cell has been observed populated.
 */

/** Fields all three shapes share. */
interface ReversalRowBase {
  tenant_id: string | null;
  customer_id: string | null;
  formula_display_text: string | null;
  governed_formula_expression: string | null;
  governed_formula_version: string | null;
  /** Absent from some questions' `cols` entirely, which reads as `null` here. */
  result_role: string | null;
}

/** `C-REV-002` — one row per reversal event, 23 columns. */
export interface ReversalEventRow extends ReversalRowBase {
  event_id: string | null;
  reversal_id: string | null;
  occurred_at: string | null;
  requested_at: string | null;
  processed_at_ts: string | null;
  entitlement_type: string | null;
  reversal_kind: string | null;
  reversal_target_type: string | null;
  reason_code: string | null;
  reversal_status: string | null;
  retry_count: number | null;
  initiated_by: string | null;
  original_event_ref: string | null;
  original_amount: number | null;
  reversed_amount: number | null;
  reconciliation_status: string | null;
  reconciliation_delta_amount: number | null;
}

/** The two aggregate shapes differ only in what they group BY. */
export interface ReversalAggregateBase extends ReversalRowBase {
  n_events: number | null;
  total_reversed_amount: number | null;
  total_original_amount: number | null;
}

/** `C-REV-003` — grouped by entitlement type, 10 columns. */
export interface ReversalEntitlementRow extends ReversalAggregateBase {
  entitlement_type: string | null;
}

/** `C-REV-004` — grouped by reason code, 10 columns. */
export interface ReversalReasonRow extends ReversalAggregateBase {
  reason_code: string | null;
}

/**
 * A discriminated union rather than one merged row type: the three shapes answer three different
 * questions and a consumer that renders an event table must not be handed an aggregate row it will
 * silently draw as an event with every column blank.
 */
export type ReversalResult =
  | { shape: 'events'; rows: ReversalEventRow[] }
  | { shape: 'byEntitlementType'; rows: ReversalEntitlementRow[] }
  | { shape: 'byReasonCode'; rows: ReversalReasonRow[] };

export type ReversalShape = ReversalResult['shape'];

/**
 * Which shape an envelope's `cols` declare, or `undefined` for anything that is not a Reversal
 * breakdown — including `C-REV-001`, whose standard row this must not claim.
 *
 * Resolved from the columns, not from the metric id: the id is a catalogue fact this layer does not
 * hold, and a question re-pointed at a different shape would keep its id.
 */
export const reversalShapeOf = (envelope: MetricEnvelope): ReversalShape | undefined => {
  const read = readerFor(tableFrom(envelope).cols);

  // `metric_value` is the standard schema's own column; its presence rules all three out.
  if (read.has('metric_value')) {
    return undefined;
  }

  if (read.has('reversal_id') || read.has('event_id')) {
    return 'events';
  }

  if (!read.has('n_events')) {
    return undefined;
  }

  if (read.has('entitlement_type')) {
    return 'byEntitlementType';
  }

  return read.has('reason_code') ? 'byReasonCode' : undefined;
};

export const reversalFromEnvelope = (envelope: MetricEnvelope): ReversalResult | undefined => {
  const shape = reversalShapeOf(envelope);

  if (!shape) {
    return undefined;
  }

  const { cols, rows: allRows } = tableFrom(envelope);
  const read = readerFor(cols);
  // `Why this result?` reason rows answer none of these columns, so one left in would list a blank
  // reversal event beside the real ones. Same partition the scalar parse makes. PLA-8828.
  const rows = readingRowsOnly(read, allRows);

  const base = (row: unknown[]): ReversalRowBase => ({
    tenant_id: read.text(row, 'tenant_id'),
    customer_id: read.text(row, 'customer_id'),
    formula_display_text: read.text(row, 'formula_display_text'),
    governed_formula_expression: read.text(row, 'governed_formula_expression'),
    governed_formula_version: read.text(row, 'governed_formula_version'),
    result_role: read.text(row, 'result_role')
  });

  if (shape === 'events') {
    return {
      shape,
      rows: rows.map(row => ({
        ...base(row),
        event_id: read.text(row, 'event_id'),
        reversal_id: read.text(row, 'reversal_id'),
        occurred_at: read.text(row, 'occurred_at'),
        requested_at: read.text(row, 'requested_at'),
        processed_at_ts: read.text(row, 'processed_at_ts'),
        entitlement_type: read.text(row, 'entitlement_type'),
        reversal_kind: read.text(row, 'reversal_kind'),
        reversal_target_type: read.text(row, 'reversal_target_type'),
        reason_code: read.text(row, 'reason_code'),
        reversal_status: read.text(row, 'reversal_status'),
        retry_count: read.number(row, 'retry_count'),
        initiated_by: read.text(row, 'initiated_by'),
        original_event_ref: read.text(row, 'original_event_ref'),
        original_amount: read.number(row, 'original_amount'),
        reversed_amount: read.number(row, 'reversed_amount'),
        reconciliation_status: read.text(row, 'reconciliation_status'),
        reconciliation_delta_amount: read.number(row, 'reconciliation_delta_amount')
      }))
    };
  }

  const aggregate = (row: unknown[]) => ({
    ...base(row),
    n_events: read.number(row, 'n_events'),
    total_reversed_amount: read.number(row, 'total_reversed_amount'),
    total_original_amount: read.number(row, 'total_original_amount')
  });

  if (shape === 'byEntitlementType') {
    return {
      shape,
      rows: rows.map(row => ({
        ...aggregate(row),
        entitlement_type: read.text(row, 'entitlement_type')
      }))
    };
  }

  return {
    shape,
    rows: rows.map(row => ({ ...aggregate(row), reason_code: read.text(row, 'reason_code') }))
  };
};

export default reversalFromEnvelope;
